import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { CheckIcon } from '@untitledui-icons/react/line'
import { PageHeader } from '@/components/layout'
import { Button } from '@/components/base/buttons'
import { FormInput, FormSelect, FormSwitch, PhoneNumberInput } from '@/components/forms'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { useIdentity } from '@/hooks/api/useIdentity'
import { useUpdateProfile } from '@/hooks/api/useProfile'
import { useLocales, useUser, getReadableLocaleName } from '@/hooks/api/useUserManagement'

// Validation schema
const profileSchema = z
  .object({
    first_name: z.string().min(1, 'app.validation.first_name_required'),
    last_name: z.string().min(1, 'app.validation.last_name_required'),
    phone_number: z.string().optional(),
    new_password: z
      .string()
      .optional()
      .refine(
        (val) => {
          if (!val || val === '') return true
          return val.length >= 8
        },
        { message: 'app.validation.minLength' }
      )
      .refine(
        (val) => {
          if (!val || val === '') return true
          return /[A-Z]/.test(val)
        },
        { message: 'app.validation.password.uppercase' }
      )
      .refine(
        (val) => {
          if (!val || val === '') return true
          return /[!@#$%^&*]/.test(val)
        },
        { message: 'app.validation.password.special' }
      ),
    confirm_password: z.string().optional(),
    locale: z.string().min(1, 'app.validation.language_required'),
    two_factor_enabled: z.boolean(),
  })
  .refine(
    (data) => {
      if (data.new_password && data.new_password !== data.confirm_password) {
        return false
      }
      return true
    },
    {
      message: 'app.validation.password.mismatch',
      path: ['confirm_password'],
    }
  )
  .refine(
    (data) => {
      if (data.two_factor_enabled && (!data.phone_number || data.phone_number.trim() === '')) {
        return false
      }
      return true
    },
    {
      message: 'app.validation.phone_required_2fa',
      path: ['phone_number'],
    }
  )

type ProfileFormValues = z.infer<typeof profileSchema>

export function ProfilePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Fetch current user identity (for user ID and email)
  const { user: identity, isLoading: identityLoading } = useIdentity()

  // Fetch full user record (includes two_factor_enabled, phone_number, etc.)
  const { data: user, isLoading: userLoading } = useUser(identity?.id ?? 0, !!identity?.id)

  // Fetch locales
  const { data: locales = [] } = useLocales()

  // Update mutation
  const updateProfile = useUpdateProfile()

  // Form setup
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, isDirty },
  } = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      phone_number: '',
      new_password: '',
      confirm_password: '',
      locale: 'en',
      two_factor_enabled: false,
    },
  })

  // Reset form when full user data loads
  useEffect(() => {
    if (user) {
      reset({
        first_name: user.first_name,
        last_name: user.last_name,
        phone_number: user.phone_number || '',
        new_password: '',
        confirm_password: '',
        locale: user.locale || 'en',
        two_factor_enabled: user.two_factor_enabled ?? false,
      })
    }
  }, [user, reset])

  // Locale options
  const localeOptions = locales.map((locale) => ({
    value: locale,
    label: getReadableLocaleName(locale),
  }))

  // Handle form submission
  const onSubmit = async (data: ProfileFormValues) => {
    try {
      await updateProfile.mutateAsync({
        first_name: data.first_name,
        last_name: data.last_name,
        phone_number: data.phone_number || undefined,
        new_password: data.new_password || undefined,
        locale: data.locale,
        two_factor_enabled: data.two_factor_enabled,
      })
      toast.success(t('resources.profile.update_success'))
      navigate('/dashboard')
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : t('resources.profile.update_error')
      toast.error(message)
    }
  }

  if (identityLoading || userLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!identity || !user) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <p className="text-gray-500 dark:text-gray-400">{t('resources.profile.unable_to_load')}</p>
        <Button color="secondary" onPress={() => navigate('/dashboard')} className="mt-4">
          {t('resources.profile.back_to_dashboard')}
        </Button>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={t('resources.generic.my_profile')}
        subtitle={t('resources.profile.subtitle')}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-auto px-6 py-4">
        <div className="mx-auto max-w-2xl space-y-8">
          {/* User Information Section */}
          <section>
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('resources.profile.user_info')}
            </h2>
            <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
              <FormInput
                control={control}
                name="first_name"
                label={t('resources.users.fields.first_name')}
                placeholder={t('resources.users.placeholders.first_name')}
                isRequired
              />

              <FormInput
                control={control}
                name="last_name"
                label={t('resources.users.fields.last_name')}
                placeholder={t('resources.users.placeholders.last_name')}
                isRequired
              />

              {/* Email - read-only */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('resources.users.fields.email')}
                </label>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
                  {user.email}
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t('resources.profile.email_readonly')}
                </p>
              </div>

              <PhoneNumberInput
                control={control}
                name="phone_number"
                label={t('resources.users.fields.phone_number')}
                description={t('resources.profile.phone_description')}
                autoComplete="off"
              />
            </div>
          </section>

          {/* Security Section */}
          <section>
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('resources.profile.security')}
            </h2>
            <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
              <FormInput
                control={control}
                name="new_password"
                label={t('resources.users.fields.new_password')}
                type="password"
                placeholder={t('resources.users.placeholders.new_password')}
                description={t('resources.users.descriptions.password')}
                autoComplete="new-password"
              />

              <FormInput
                control={control}
                name="confirm_password"
                label={t('resources.users.fields.password_confirmation')}
                type="password"
                placeholder={t('resources.profile.confirm_password_placeholder')}
                autoComplete="new-password"
              />

              <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                <FormSwitch
                  control={control}
                  name="two_factor_enabled"
                  label={t('resources.users.fields.requires_2fa')}
                  description={t('resources.profile.two_factor_description')}
                />
              </div>
            </div>
          </section>

          {/* System Preferences Section */}
          <section>
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('resources.profile.system_preferences')}
            </h2>
            <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
              <FormSelect
                control={control}
                name="locale"
                label={t('resources.profile.language')}
                options={localeOptions}
                isRequired
              />
            </div>
          </section>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-6 dark:border-gray-700">
            <Button color="secondary" type="button" onPress={() => navigate('/dashboard')}>
              {t('resources.generic.cancel')}
            </Button>
            <Button color="primary" type="submit" isDisabled={isSubmitting || !isDirty} iconLeading={CheckIcon}>
              {isSubmitting ? t('resources.profile.saving') : t('resources.profile.save_changes')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}

export default ProfilePage
